import moment from 'moment';

import { EmpBusinessUnit, EmpPosition, EmploymentData } from '~/data/employee';
import { EmploymentDetailType } from '~/data/employment/useEmploymentDetails';
import { AllEnums, Enum, EnumListKey } from '~/data/enums';
import { OrgTreeData } from '~/data/orgunits/types';
import { ProposalProps, ProposalType } from '~/data/proposals';
import { empPositionNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data/newTypeToLegacy';
import {
  ScheduleEmploymentFormCostCenterDetails,
  ScheduleEmploymentFormUnitPositionDetails,
  ScheduleEmploymentFormValues,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { emplDetailsNewTypeToLegacy } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/data/newTypeToLegacy';
import { ScheduleEmploymentDetailFormValues } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import {
  BusinessUnitData,
  PendingScheduleChanges,
  ScheduleForm,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { convertToDateOrNull, filterActiveEnums } from '~/utils/helper';

type PropsType = {
  empCurrentData: EmpPosition;
  employmentCurrentData: EmploymentData;
  employmentCurrentDetail: EmploymentDetailType;
  fEmpBusinessUnits: EmpBusinessUnit[];
  orgTree: OrgTreeData[];
  allEnums: AllEnums;
  contractNumber: string | undefined;
  personNumber: string;
  pendingData: PendingScheduleChanges[];
  mainContract: boolean;
  employmentDetailsProposal?: ProposalProps;
  empPositionProposal?: ProposalProps;
  defaultResponsibleHrId?: number;
};

export const mapDefaultData = (props: PropsType): ScheduleForm[] => {
  const {
    empCurrentData,
    employmentCurrentData,
    employmentCurrentDetail,
    fEmpBusinessUnits,
    orgTree,
    allEnums,
    contractNumber,
    personNumber,
    pendingData,
    mainContract,
    defaultResponsibleHrId,
    employmentDetailsProposal,
    empPositionProposal,
  } = props;

  //Current empl data
  const empData =
    empPositionProposal &&
    empPositionProposal.proposalType === ProposalType.POSITION_INTERNAL_PROPOSAL
      ? empPositionNewTypeToLegacy(empPositionProposal)
      : pendingData?.[0]?.empPosition || empCurrentData;
  const employmentData = employmentCurrentData;
  const employmentDetail: EmploymentDetailType =
    employmentDetailsProposal &&
    employmentDetailsProposal.proposalType ===
      ProposalType.EMPLOYMENT_DETAILS_PROPOSAL
      ? emplDetailsNewTypeToLegacy(employmentDetailsProposal)
      : pendingData?.[0]?.employmentDetails || employmentCurrentDetail;
  // end current empl data
  const defaultCC = empData?.fEmpCostCenters?.find(
    (cc) => cc.fEmpCostCenterIsDefault,
  );
  const businessUnits: BusinessUnitData = (orgTree ?? [])
    .slice(1)
    .reduce((acc, bu) => {
      const selectedBU = fEmpBusinessUnits.find(
        (ebu) => ebu.treeId === bu.fTreeId,
      );
      const key = `fEmpBusinessUnit${bu.fTreeId}` as string;
      return {
        ...acc,
        [key]: selectedBU ? selectedBU.unitId.toString() : '-1',
      };
    }, {});

  const employmentStatuses = filterActiveEnums(
    allEnums[EnumListKey.EMPLOYMENT_STATUS],
  );
  const jobGrades = filterActiveEnums(allEnums[EnumListKey.JOB_GRADE]);

  const getPersonnelGroupEnums = (groupNumber: number) => {
    const isValidGroupNumber = groupNumber >= 1 && groupNumber <= 5;
    return isValidGroupNumber
      ? filterActiveEnums(allEnums[`PERSONNEL_GROUP_${groupNumber}`])
      : [];
  };

  const processEmploymentDataCommonValues = (employment: EmploymentData) => {
    return {
      fEmplTerminationDate: convertToDateOrNull(
        employment?.fEmplTerminationDate,
      ),
      fEmplTerminationReason: employment?.fEmplTerminationReason || null,
      fEmplDateArrival: convertToDateOrNull(employment?.fEmplDateArrival),
      fEmplTrialPeriodEndDate: convertToDateOrNull(
        employment?.fEmplTrialPeriodEndDate,
      ),
      fEmplDateStart: convertToDateOrNull(employment?.fEmplDateStart),
      fEmplTrialPeriod: employment?.fEmplTrialPeriod || null,
      fEmplPlannedEndDate: convertToDateOrNull(employment?.fEmplPlannedEndDate),
      fEmplLastDateEmployment: convertToDateOrNull(
        employment?.fEmplLastDateEmployment,
      ),
      fEmplFixedTermReason: employment?.fEmplFixedTermReason || null,
      fEmplInfo: employment?.fEmplInfo || '',
      fEmplType: employment?.fEmplType || null,
    };
  };

  const processEmploymentDetailsCommonValues = (
    employmentDetails: EmploymentDetailType,
  ): ScheduleEmploymentDetailFormValues => {
    const toValidEnumCode = (enums: Enum[], value?: string): string =>
      enums.find((el) => el.code === value)?.code || '';

    return {
      fEmplDetailTaxNumber: employmentDetails?.fEmplDetailTaxNumber || '',
      fEmplDetailStartDate: employmentDetails?.fEmplDetailIsPending
        ? moment(employmentDetails.fEmplDetailStartDate).toDate()
        : moment(new Date()).toDate(),
      fEmplDetailCalcHoursBalance:
        employmentDetails?.fEmplDetailCalcHoursBalance ? 'true' : '',
      fEmplDetailVacBalCalcSixDays:
        employmentDetails?.fEmplDetailVacBalCalcSixDays,
      fEmplDetailJobGrade: toValidEnumCode(
        jobGrades,
        employmentDetails?.fEmplDetailJobGrade,
      ),
      fEmplDetailStatus: toValidEnumCode(
        employmentStatuses,
        employmentDetails?.fEmplDetailStatus,
      ),
      fEmplDetailPersonnelGroup1: toValidEnumCode(
        getPersonnelGroupEnums(1),
        employmentDetails?.fEmplDetailPersonnelGroup1,
      ),
      fEmplDetailPersonnelGroup2: toValidEnumCode(
        getPersonnelGroupEnums(2),
        employmentDetails?.fEmplDetailPersonnelGroup2,
      ),
      fEmplDetailPersonnelGroup3: toValidEnumCode(
        getPersonnelGroupEnums(3),
        employmentDetails?.fEmplDetailPersonnelGroup3,
      ),
      fEmplDetailPersonnelGroup4: toValidEnumCode(
        getPersonnelGroupEnums(4),
        employmentDetails?.fEmplDetailPersonnelGroup4,
      ),
      fEmplDetailPersonnelGroup5: toValidEnumCode(
        getPersonnelGroupEnums(5),
        employmentDetails?.fEmplDetailPersonnelGroup5,
      ),
      fEmplId: employmentDetails.fEmplId,
      customData: employmentDetails?.fEmplDetailCustomData || {},
    };
  };

  const processEmploymentPositionCommonValues = (
    employmentPosition: EmpPosition,
  ): Omit<ScheduleEmploymentFormValues, 'fInternalPositionStartDate'> => {
    const {
      fInternalPositionPosition,
      fEmpJobTitle,
      fInternalPositionInfo,
      fInternalPositionUnit,
    } = employmentPosition ?? {};

    const unitPositionDetails: ScheduleEmploymentFormUnitPositionDetails[] =
      empData?.fEmpUnitPositions.map(
        (up): ScheduleEmploymentFormUnitPositionDetails => {
          const { fEmpUnitPos, fEmpUnitPosPercent } = up;

          return {
            unitPosition: fEmpUnitPos,
            unitPositionPercent: fEmpUnitPosPercent,
          };
        },
      ) || [];
    const totalUnitPositionPercentage: number = unitPositionDetails.reduce(
      (
        total: number,
        item: ScheduleEmploymentFormUnitPositionDetails,
      ): number => {
        const { unitPositionPercent } = item;
        return total + (unitPositionPercent ?? 0);
      },
      0,
    );

    const costCenterDetails: ScheduleEmploymentFormCostCenterDetails[] =
      employmentPosition?.fEmpCostCenters?.map(
        (cc): ScheduleEmploymentFormCostCenterDetails => {
          const {
            fEmpCostCenter,
            fEmpCostCenterPercent,
            fEmpCostCenterCustomData,
            fEmpCostCenterIsDefault,
          } = cc;
          return {
            costCenterId: fEmpCostCenter,
            percent: fEmpCostCenterPercent,
            customData: fEmpCostCenterCustomData,
            isDefault: fEmpCostCenterIsDefault,
          };
        },
      ) || [];
    const totalCostCenterPercentage: number = costCenterDetails.reduce(
      (
        total: number,
        item: ScheduleEmploymentFormCostCenterDetails,
      ): number => {
        const { percent } = item;
        return total + (percent ?? 0);
      },
      0,
    );

    return {
      fEmpLegalEntity: employmentPosition?.fEmpLegalEntity?.toString(),
      fInternalPositionPosition,
      fEmpJobTitle,
      fInternalPositionInfo,
      fInternalPositionUnit,
      fInternalPositionManager:
        employmentPosition?.fInternalPositionManager?.toString(),
      fEmpCostCenter: defaultCC ? defaultCC.fEmpCostCenter : -1,
      costCenterDetails,
      totalCostCenterPercentage,
      unitPositionDetails,
      totalUnitPositionPercentage,
    };
  };

  const additionalEmployments = (pendingData ?? [])
    .slice(1)
    .map((additionalData) => {
      if (!Object.keys(additionalData).length) {
        return {};
      }

      const additionalEmployment = additionalData?.employment;
      const additionalEmploymentDetails = additionalData?.employmentDetails;
      const additionalEmpPosition = additionalData?.empPosition;

      return {
        empPosition: {
          ...processEmploymentPositionCommonValues(additionalEmpPosition),
          fInternalPositionStartDate:
            additionalEmpPosition.fInternalPositionIsPending
              ? moment(
                  additionalEmpPosition.fInternalPositionStartDate,
                ).toDate()
              : moment(new Date()).toDate(),
        },
        employment: processEmploymentDataCommonValues(additionalEmployment),
        employmentDetails: processEmploymentDetailsCommonValues(
          additionalEmploymentDetails,
        ),
      };
    })
    .filter((data) => !!Object.keys(data).length);

  return [
    {
      empPosition: {
        ...processEmploymentPositionCommonValues(empData),
        fInternalPositionStartDate:
          empData?.fInternalPositionIsPending || empPositionProposal
            ? moment(empData?.fInternalPositionStartDate).toDate()
            : moment(new Date()).toDate(),
        fInternalPositionProposalHrId: defaultResponsibleHrId,
      },
      employment: processEmploymentDataCommonValues(employmentData),
      employmentDetails: {
        ...processEmploymentDetailsCommonValues(employmentDetail),
        fEmplDetailProposalHrId: defaultResponsibleHrId,
        customData: employmentDetail?.fEmplDetailCustomData || {},
      },
      fEmpContractNumber: contractNumber || '',
      fEmpPersonNumber: personNumber || '',
      mainContract: mainContract || false,
      ...businessUnits,
      meta: {
        positionProposal: {
          proposalId: empPositionProposal?.proposalId,
          proposalStatus: empPositionProposal?.proposalStatus,
        },
        detailsProposal: {
          proposalId: employmentDetailsProposal?.proposalId,
          proposalStatus: employmentDetailsProposal?.proposalStatus,
        },
      },
    },
    ...additionalEmployments,
  ];
};
